'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import {
  ArrowLeft, Save, X, AlertTriangle, CheckCircle2, Bird, Info
} from 'lucide-react';
import { Card } from '@/components/ui/Cards';
import {
  MOVEMENT_TYPE_LABELS, MOCK_FLOCK_SUMMARIES, FARMS, HOUSES_BY_FARM,
  BIRD_TYPES, CULLING_REASONS, type BirdMovementType
} from '../../../components/birdRecordsData';

const MOVEMENT_TYPES: BirdMovementType[] = [
  'initial_placement', 'purchase', 'hatch', 'transfer_in', 'transfer_out',
  'mortality', 'culling', 'sale', 'return', 'positive_adjustment', 'negative_adjustment', 'other'
];

const DIRECTION_MAP: Record<BirdMovementType, 'in' | 'out' | 'neutral'> = {
  initial_placement: 'in', purchase: 'in', hatch: 'in', transfer_in: 'in', return: 'in', positive_adjustment: 'in',
  transfer_out: 'out', mortality: 'out', culling: 'out', sale: 'out', negative_adjustment: 'out',
  reversal: 'neutral', other: 'neutral',
};

export default function NewBirdMovementContent() {
  const [movementType, setMovementType] = useState<BirdMovementType>('mortality');
  const [farm, setFarm] = useState('Idudi Poultry Farm');
  const [flock, setFlock] = useState('flock-002');
  const [quantity, setQuantity] = useState(0);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [approvalRequired, setApprovalRequired] = useState(false);

  const selectedFlock = MOCK_FLOCK_SUMMARIES.find(f => f.flockId === flock);
  const direction = DIRECTION_MAP[movementType];
  const populationBefore = selectedFlock?.currentBirds ?? 0;
  const populationAfter = direction === 'in'
    ? populationBefore + quantity
    : direction === 'out'
      ? populationBefore - quantity
      : populationBefore;

  const isNegative = populationAfter < 0;
  const isOverCapacity = selectedFlock && direction === 'in' && populationAfter > selectedFlock.houseCapacity;
  const isLargeAdjustment = (movementType === 'positive_adjustment' || movementType === 'negative_adjustment') && quantity > 50;

  const nextMovementNumber = 'BRM-2026-000016';

  return (
    <div className="space-y-4 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Link href="/bird-records/movements" className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground transition-colors">
          <ArrowLeft size={14} />
        </Link>
        <div>
          <h1 className="text-lg font-semibold text-foreground">Record Bird Movement</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Movement # <span className="font-medium text-primary">{nextMovementNumber}</span> · Auto-generated
          </p>
        </div>
      </div>

      {/* Validation Warnings */}
      {isNegative && (
        <div className="flex items-start gap-2 p-3 rounded-xl border border-red-200 bg-red-50">
          <AlertTriangle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-red-700">Negative Population Warning</p>
            <p className="text-xs text-red-600 mt-0.5">
              This movement would result in a negative bird count ({populationAfter.toLocaleString()}). Please verify the quantity.
            </p>
          </div>
        </div>
      )}
      {isOverCapacity && (
        <div className="flex items-start gap-2 p-3 rounded-xl border border-amber-200 bg-amber-50">
          <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-amber-700">House Capacity Warning</p>
            <p className="text-xs text-amber-600 mt-0.5">
              This movement would exceed house capacity ({populationAfter.toLocaleString()} / {selectedFlock?.houseCapacity.toLocaleString()} birds). Authorization required.
            </p>
          </div>
        </div>
      )}
      {isLargeAdjustment && (
        <div className="flex items-start gap-2 p-3 rounded-xl border border-amber-200 bg-amber-50">
          <Info size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-amber-700">Large Adjustment — Approval Required</p>
            <p className="text-xs text-amber-600 mt-0.5">
              Adjustments over 50 birds require management approval before posting.
            </p>
          </div>
        </div>
      )}

      {/* Section A — Movement */}
      <Card padding="md">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">A — Movement Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Movement Date *</label>
            <input
              type="date"
              defaultValue="2026-09-06"
              className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Movement Type *</label>
            <select
              value={movementType}
              onChange={(e) => setMovementType(e.target.value as BirdMovementType)}
              className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
            >
              {MOVEMENT_TYPES.map(t => (
                <option key={t} value={t}>{MOVEMENT_TYPE_LABELS[t]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Farm *</label>
            <select
              value={farm}
              onChange={(e) => setFarm(e.target.value)}
              className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
            >
              {FARMS.map(f => <option key={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Poultry House *</label>
            <select className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40">
              {(HOUSES_BY_FARM[farm] ?? []).map(h => <option key={h}>{h}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-muted-foreground block mb-1">Flock *</label>
            <select
              value={flock}
              onChange={(e) => setFlock(e.target.value)}
              className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
            >
              <option value="">Select flock...</option>
              {MOCK_FLOCK_SUMMARIES.filter(f => f.farm === farm).map(f => (
                <option key={f.flockId} value={f.flockId}>
                  {f.flockNumber} — {f.flockName} (Current: {f.currentBirds.toLocaleString()})
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Section B — Birds */}
      <Card padding="md">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">B — Bird Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Bird Type *</label>
            <select className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40">
              {BIRD_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Breed</label>
            <input
              type="text"
              defaultValue="Ross 308"
              className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Quantity *</label>
            <input
              type="number"
              min={1}
              value={quantity || ''}
              onChange={(e) => setQuantity(Number(e.target.value))}
              placeholder="0"
              className={`w-full text-xs border rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40 ${isNegative ? 'border-red-400' : 'border-border/60'}`}
            />
          </div>
        </div>

        {/* Population Preview */}
        {selectedFlock && quantity > 0 && (
          <div className="mt-3 p-3 rounded-xl bg-muted/20 border border-border/60">
            <p className="text-xs font-semibold text-foreground mb-2">Population Impact Preview</p>
            <div className="flex items-center gap-4 text-xs">
              <div>
                <p className="text-muted-foreground">Current Population</p>
                <p className="font-tabular font-semibold text-foreground">{populationBefore.toLocaleString()}</p>
              </div>
              <div className="text-muted-foreground text-lg">→</div>
              <div>
                <p className="text-muted-foreground">This Movement</p>
                <p className={`font-tabular font-semibold ${direction === 'in' ? 'text-green-600' : 'text-red-600'}`}>
                  {direction === 'in' ? '+' : direction === 'out' ? '-' : ''}{quantity.toLocaleString()}
                </p>
              </div>
              <div className="text-muted-foreground text-lg">→</div>
              <div>
                <p className="text-muted-foreground">New Population</p>
                <p className={`font-tabular font-bold text-base ${isNegative ? 'text-red-600' : 'text-green-700'}`}>
                  {populationAfter.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Section C — Movement Details */}
      <Card padding="md">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">C — Movement Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(movementType === 'transfer_in' || movementType === 'transfer_out') && (
            <>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Source Farm</label>
                <select className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40">
                  {FARMS.map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Source House</label>
                <select className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40">
                  {HOUSES_BY_FARM['Idudi Poultry Farm'].map(h => <option key={h}>{h}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Destination Farm</label>
                <select className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40">
                  {FARMS.map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Destination House</label>
                <select className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40">
                  {HOUSES_BY_FARM['Idudi Poultry Farm'].map(h => <option key={h}>{h}</option>)}
                </select>
              </div>
            </>
          )}
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">
              Reason {(movementType === 'positive_adjustment' || movementType === 'negative_adjustment') ? '*' : ''}
            </label>
            {movementType === 'culling' ? (
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
              >
                <option value="">Select reason...</option>
                {CULLING_REASONS.map(r => <option key={r}>{r}</option>)}
              </select>
            ) : (
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason for this movement..."
                className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
              />
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Reference (PO / Sale / Mortality #)</label>
            <input
              type="text"
              placeholder="e.g. MC-2026-090"
              className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-muted-foreground block mb-1">Notes</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes..."
              className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40 resize-none"
            />
          </div>
        </div>
      </Card>

      {/* Section D — Authorization */}
      <Card padding="md">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">D — Authorization</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Recorded By</label>
            <input
              type="text"
              defaultValue="James Okello"
              className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
            />
          </div>
          <div className="flex items-center gap-3 pt-4">
            <input
              type="checkbox"
              id="approvalRequired"
              checked={approvalRequired || isLargeAdjustment}
              onChange={(e) => setApprovalRequired(e.target.checked)}
              className="rounded"
            />
            <label htmlFor="approvalRequired" className="text-xs font-medium text-foreground">
              Approval Required Before Posting
            </label>
          </div>
          {(approvalRequired || isLargeAdjustment) && (
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Approver *</label>
              <select className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40">
                <option>Farm Manager</option>
                <option>Farm Owner</option>
              </select>
            </div>
          )}
        </div>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 pb-4">
        <button
          disabled={isNegative || quantity <= 0}
          className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <CheckCircle2 size={12} />
          Submit Movement
        </button>
        <button className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium border border-border/60 rounded-lg bg-card hover:bg-muted/50 transition-colors">
          <Save size={12} />
          Save Draft
        </button>
        <button className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium border border-border/60 rounded-lg bg-card hover:bg-muted/50 transition-colors">
          Submit & Add Another
        </button>
        <Link href="/bird-records/movements" className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
          <X size={12} />
          Cancel
        </Link>
      </div>
    </div>
  );
}
